import React, { memo, useCallback, useMemo, useState, useSyncExternalStore } from "react";
import cx from "classnames";
import {
  MAX_ENTRIES,
  clearDispatchLog,
  getDispatchSnapshot,
  getEntrySource,
  setDispatchMonitoringEnabled,
  subscribeToDispatchLog,
} from "../../../document/dispatchMonitor";
import { collapseLibraryFrames, parseStack } from "../../../utils/stackFrames";
import formatTime from "../formatTime";
import EnableMonitoringPanel from "../RefluxMonitoring/EnableMonitoringPanel";
import JsonTree from "../RefluxMonitoring/JsonTree";
import refluxStyles from "../RefluxMonitoring/RefluxMonitoring.module.css";
import styles from "./DispatchMonitoring.module.css";

/** Durations at or above this are flagged as slow (one 60fps frame). */
const SLOW_MS = 16;

/**
 * @param {number} ms
 * @returns {string}
 */
const formatDuration = (ms) => (ms < 10 ? `${ms.toFixed(1)}ms` : `${Math.round(ms)}ms`);

/**
 * Call stack with library frames folded away unless `showAll` is on.
 * @param {Object} props
 * @param {string} props.stack - raw stack string
 */
const CallStack = ({ stack }) => {
  const [showAll, setShowAll] = useState(false);
  const frames = useMemo(() => parseStack(stack), [stack]);
  const rows = showAll
    ? frames.map((frame) => ({ kind: "frame", frame }))
    : collapseLibraryFrames(frames);
  const hasLibraryFrames = frames.some((frame) => frame.library);

  if (frames.length === 0) return <pre className={styles.stack}>{stack}</pre>;

  return (
    <div className={styles.stack} data-testid="dispatch-monitoring-stack">
      {rows.map((row, i) =>
        row.kind === "library" ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and rebuilt together
          <div key={i} className={styles.libraryFrames}>
            {row.count} library {row.count === 1 ? "frame" : "frames"}
          </div>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and rebuilt together
          <div key={i} className={cx(styles.frame, { [styles.frameLibrary]: row.frame.library })}>
            <span className={styles.frameFn}>{row.frame.fn || "(anonymous)"}</span>
            <span className={styles.frameLocation}>{row.frame.location}</span>
          </div>
        ),
      )}
      {hasLibraryFrames && (
        <button type="button" className={styles.stackToggle} onClick={() => setShowAll(!showAll)}>
          {showAll ? "Hide library frames" : "Show all frames"}
        </button>
      )}
    </div>
  );
};

/**
 * The recorded payload, parsed only when shown.
 * @param {Object} props
 * @param {import("../../../document/dispatchMonitor").PayloadSnapshot} props.payload
 */
const PayloadView = ({ payload }) => {
  const value = useMemo(() => (payload.json ? JSON.parse(payload.json) : undefined), [payload]);
  if (payload.size) {
    return (
      <span className={styles.note}>
        Too large to keep ({Math.round(payload.size / 1024)} KB of JSON)
      </span>
    );
  }
  if (payload.error) {
    return <span className={styles.note}>Couldn't copy payload: {payload.error}</span>;
  }
  if (value === undefined) {
    return <span className={styles.note}>None. This command takes no payload.</span>;
  }
  return <JsonTree data={value} />;
};

/**
 * Payload and call stack for one dispatch.
 * @param {Object} props
 * @param {Object|null} props.entry - selected log entry
 */
const EntryDetail = ({ entry }) => {
  if (!entry) {
    return <div className={refluxStyles.emptyState}>Select a dispatch to see its payload</div>;
  }
  const source = getEntrySource(entry);
  const stack = entry.callSite?.stack;
  return (
    <div className={styles.detail} data-testid="dispatch-monitoring-detail">
      <div className={styles.detailTitle}>
        <span className={styles.detailType}>{entry.type}</span>
        <span className={styles.detailMeta}>
          {formatTime(entry.time, { ms: true })} · {formatDuration(entry.durationMs)}
          {source ? ` · ${source}` : ""}
        </span>
      </div>
      {entry.error && <div className={styles.detailError}>Threw: {entry.error}</div>}
      <div className={styles.sectionLabel}>Payload</div>
      <div className={refluxStyles.stateContent}>
        <PayloadView payload={entry.payload} />
      </div>
      {stack && (
        <>
          <div className={styles.sectionLabel}>Call stack</div>
          <CallStack key={entry.id} stack={stack} />
        </>
      )}
    </div>
  );
};

/**
 * One line of the log. Memoized: entries keep their identity across updates,
 * so only new or re-selected rows render.
 * @param {Object} props
 * @param {Object} props.entry
 * @param {boolean} props.selected
 * @param {(id: number) => void} props.onSelect
 */
const LogRow = memo(({ entry, selected, onSelect }) => (
  <button
    type="button"
    className={cx(styles.logRow, {
      [styles.logRowSelected]: selected,
      [styles.logRowError]: entry.error,
    })}
    onClick={() => onSelect(entry.id)}
    data-testid="dispatch-monitoring-row"
  >
    <span className={styles.colTime}>{formatTime(entry.time, { ms: true })}</span>
    <span className={styles.colType}>{entry.type}</span>
    <span
      className={cx(styles.colDuration, { [styles.slow]: entry.durationMs >= SLOW_MS })}
      title="Until the first round of handlers finished"
    >
      {formatDuration(entry.durationMs)}
    </span>
    <span className={styles.colSource}>{getEntrySource(entry) ?? ""}</span>
  </button>
));

/**
 * A command total in the left column; click to show only that command.
 * @param {Object} props
 * @param {string} props.label
 * @param {number} props.count
 * @param {boolean} props.selected
 * @param {() => void} props.onClick
 */
const CountRow = ({ label, count, selected, onClick }) => (
  <button
    type="button"
    className={cx(styles.countRow, { [styles.countRowSelected]: selected })}
    onClick={onClick}
  >
    <span className={refluxStyles.storeName}>{label}</span>
    <span
      className={cx(refluxStyles.storeCount, styles.count, {
        [refluxStyles.storeCountSelected]: selected,
      })}
    >
      {count}
    </span>
  </button>
);

/**
 * DevTools tab listing recent DocumentStore dispatches.
 */
const DispatchMonitoring = () => {
  const { enabled, log, counts, total } = useSyncExternalStore(
    subscribeToDispatchLog,
    getDispatchSnapshot,
  );
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const toggleSelected = useCallback(
    (id) => setSelectedId((current) => (current === id ? null : id)),
    [],
  );

  const visible = useMemo(() => {
    const text = filter.trim().toLowerCase();
    return log.filter(
      (entry) =>
        (!typeFilter || entry.type === typeFilter) &&
        (!text ||
          entry.type.toLowerCase().includes(text) ||
          getEntrySource(entry)?.toLowerCase().includes(text)),
    );
  }, [log, filter, typeFilter]);

  const selected = log.find((entry) => entry.id === selectedId) ?? null;

  if (!enabled) {
    return (
      <EnableMonitoringPanel
        title="Dispatch recording is off"
        description="Records every design edit sent through DocumentStore.dispatch: the command, its payload, where it was called from, and how long its handlers took. Starts immediately, no reload needed."
        buttonLabel="Start recording"
        testId="dispatch-monitoring-start"
        onEnable={() => setDispatchMonitoringEnabled(true)}
      />
    );
  }

  return (
    <div className={refluxStyles.container} data-testid="dispatch-monitoring">
      <div className={refluxStyles.mainLayout}>
        <div className={cx(refluxStyles.leftPanel, styles.countsPanel)}>
          <div className={refluxStyles.leftPanelHeader}>
            <span className={styles.headerLabel}>Commands</span>
          </div>
          <div className={refluxStyles.storeList} data-testid="dispatch-monitoring-counts">
            <CountRow
              label="All"
              count={total}
              selected={!typeFilter}
              onClick={() => setTypeFilter(null)}
            />
            {counts.map(({ type, count }) => (
              <CountRow
                key={type}
                label={type}
                count={count}
                selected={typeFilter === type}
                onClick={() => setTypeFilter(typeFilter === type ? null : type)}
              />
            ))}
          </div>
        </div>

        <div className={refluxStyles.middlePanel}>
          <div className={refluxStyles.filterRow}>
            <input
              type="text"
              className={refluxStyles.filterInput}
              placeholder="Filter by command or file"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="dispatch-monitoring-filter"
            />
            <span className={refluxStyles.subsCount}>
              {visible.length} of last {MAX_ENTRIES}
            </span>
            <button
              type="button"
              className={styles.toolbarButton}
              onClick={() => {
                clearDispatchLog();
                setSelectedId(null);
              }}
              data-testid="dispatch-monitoring-clear"
            >
              Clear
            </button>
            <button
              type="button"
              className={styles.toolbarButton}
              onClick={() => setDispatchMonitoringEnabled(false)}
              data-testid="dispatch-monitoring-stop"
            >
              Stop
            </button>
          </div>
          <div className={cx(refluxStyles.contentPanel, styles.logPanel)}>
            {visible.length === 0 ? (
              <div className={refluxStyles.emptyState}>
                {log.length === 0 ? "No dispatches yet. Make an edit." : "No dispatches match"}
              </div>
            ) : (
              <div className={refluxStyles.actionLog} data-testid="dispatch-monitoring-log">
                {visible.map((entry) => (
                  <LogRow
                    key={entry.id}
                    entry={entry}
                    selected={entry.id === selectedId}
                    onSelect={toggleSelected}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={cx(refluxStyles.contentPanel, styles.detailPanel)}>
          <EntryDetail entry={selected} />
        </div>
      </div>
    </div>
  );
};

export default DispatchMonitoring;
